import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';

import { CSSProperties, useCallback, useState, useEffect } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { Link } from 'react-router-dom';
import { UpdateRegistrationSteps } from "../../../../../../../connection/create-event";
import { chooseRegistrationStep, toggleCustomizeFormModal, updateRegistrationSteps } from "../../../../../../../store/actions/admin/create-event";
import { setRegistrationOn } from "../../../../../../../store/actions/admin/create-event";
import TabHeader from "../../../../../../general-ui/tab-header/tab-header";
import { EventsState } from "../../../../../../../store/types";
import { RegistrationStep, RegistrationStepType, REGISTRATION_STEP_NAME_MAP } from "../../../../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../../../../general-ui/icon";
import Switch from "../../../../../../general-ui/switch/switch";
import { capitalize } from "../../../../../../../utils/utils";
import { PATHNAMES } from "../../../../../admin";
import PanelHeader from "../headers/panel-header";
import ModalComponent from "../../../../../../general-ui/modal/modal";
import { ParamsProps } from "../../../../../../live-event/live-event";
import "./registration-panel.scss";


const SortableStepItem: React.FC<{ step: RegistrationStep; }> = ({ step }) => {
	const {
		CreateEventReducer: {
			workingEvent,
			registrationStep
		},
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const registrationSteps = workingEvent?.registration_steps;
	const isGeneral = step.type === RegistrationStepType.general;
	const isNotEditable = step.type === RegistrationStepType.avatar || step.type === RegistrationStepType.ticketing;
	const dispatch = useDispatch();

	const { attributes, listeners, setNodeRef, transform } = useSortable({
		id: step.type,
		disabled: isGeneral
	});
	const sortableStyling: CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	async function handleToggle(_value: RegistrationStepType, _isOn: boolean) {
		if (!registrationSteps?.length || !workingEvent) return;
		const updatedSteps = registrationSteps.map(_step => {
			if (_step.type !== _value) return _step;
			return {
				..._step,
				isOn: _isOn
			};
		});
		//safety check so when you turn off a page you're on it defaults to general
		if (_value === registrationStep && !_isOn) dispatch(chooseRegistrationStep(RegistrationStepType.general));
		dispatch(updateRegistrationSteps(updatedSteps));
		await UpdateRegistrationSteps(token!, workingEvent.uuid, updatedSteps);
	}

	function handleEdit() {
		batch(() => {
			dispatch(chooseRegistrationStep(step.type));
			if (step.type === RegistrationStepType.general || step.type === RegistrationStepType.profile) {
				dispatch(toggleCustomizeFormModal(true));
			}
		});
	}

	return (
		<div
			className="card-container"
			ref={isGeneral ? null : setNodeRef}
			style={isGeneral ? {} : sortableStyling}
		>

			<div className="drag-icon" {...attributes} {...listeners} style={isGeneral ? { opacity: 0 } : {}}>
				<Icon
					name={ICONS.DRAG_HANDLE}
					size={16}
					color={COLORS.GRAY}
				/>
			</div>
			<div className="toggle-card">
				<label>{REGISTRATION_STEP_NAME_MAP[step.type]}</label>
				<div>
					{!isNotEditable && (
						<button
							disabled={!step.isOn}
							className="small no-style edit-button"
							onClick={handleEdit}
						>
							<Icon name={ICONS.EDIT} color={COLORS.CYAN} size={16} />
						</button>
					)}
					{!isGeneral &&
						<Switch
							on={step.isOn}
							onClick={handleToggle}
							value={step.type}
						/>
					}
				</div>
			</div>

		</div>
	);
};

const RegistrationPanel: React.FC = () => {
	const {
		CreateEventReducer: {
			workingEvent
		},
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const registrationOn = workingEvent?.registration_on || false;
	const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
	const registrationSteps = workingEvent?.registration_steps;

	const registrationTabs: string[] = ["Steps", "Settings"];
	const { id, tab, language } = useParams() as ParamsProps;
	const [activeTab, setActiveTab] = useState(tab ? capitalize(tab) : registrationTabs[0]);

	const history = useHistory();

	useEffect(() => {
		if (!id) return;
		history.replace(PATHNAMES.Event.RegistrationLink(id, language, activeTab.toLowerCase()));
	}, [history, id, activeTab, language]);

	const dispatch = useDispatch();

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	const handleDragEnd = useCallback(async (event: any) => {
		if (!registrationSteps) return;
		const _steps = registrationSteps.slice();

		const startIndex = _steps.findIndex((step: RegistrationStep) => step.type === event.active.id);
		const newIndex = _steps.findIndex((step: RegistrationStep) => step.type === event.over.id);

		const [item] = _steps.splice(startIndex, 1);
		_steps.splice(newIndex, 0, item);

		dispatch(updateRegistrationSteps(_steps));
		await UpdateRegistrationSteps(token!, workingEvent!.uuid, _steps);
	}, [registrationSteps, token, dispatch, workingEvent]);

	const registrationToggle = {
		value: "Registration",
		on: registrationOn,
		handleClick: () => setRegistrationModalOpen(true)
	};

	const handleRegistrationToggle = () => {
		dispatch(setRegistrationOn(!registrationOn));
		setRegistrationModalOpen(false);
	};

	// const addStep = () => {
	// 	//TODO: add functionality post MVP
	// };

	interface stepsReduceProp { generalStep: RegistrationStep, nonRequiredSteps: RegistrationStep[]; }
	const reduced = registrationSteps?.reduce((acc, cur) => {
		if (!acc.nonRequiredSteps?.length) acc.nonRequiredSteps = [];
		if (cur.type === RegistrationStepType.general) {
			acc.generalStep = cur;
		} else {
			acc.nonRequiredSteps.push(cur);
		}
		return acc;
	}, {} as stepsReduceProp);
	//deconstructing seperatly to force types
	const { generalStep, nonRequiredSteps } = reduced as stepsReduceProp || {};

	return (
		<div className="registration-panel-container">
			<PanelHeader title="Registration" useToggle={registrationToggle} >
				{registrationOn && <TabHeader
					tabs={registrationTabs}
					currentTab={activeTab}
					onClick={(tab: string) => setActiveTab(tab)}
				/>}
			</PanelHeader>
			{registrationOn ? (
				<div className="panel-body">
					{activeTab === "Steps" && (<>
						<div className="registration-steps-header">
							<label> Registration Steps</label>
							{/* FUTURE DEV */}
							{/* <button className="no-style" onClick={addStep}>
								<Icon name={ICONS.ADD} size={10} color={COLORS.CYAN} />
								Add step
							</button> */}
						</div>
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							{registrationSteps?.length &&
								<SortableContext
									items={registrationSteps.map((step: RegistrationStep) => step.type)}
									strategy={verticalListSortingStrategy}
								>
									<SortableStepItem step={generalStep} />
									{nonRequiredSteps?.length && nonRequiredSteps.map(step => <SortableStepItem step={step} key={step.type} />)}
								</SortableContext>
							}
						</DndContext>
					</>)}
					{activeTab === "Settings" && (<>
						<div className="settings-card no-style">
							<div>
								<label>Event availability</label>
								<p>Registration settings</p>
							</div>
							<Link role="button" to={PATHNAMES.Event.AvailabilityLink(id, language)} className="clear no-style">
								<Icon name={ICONS.ARROW_RIGHT} size={14} color={COLORS.WHITE} />
							</Link>
						</div>
						<div className="settings-card no-style">
							<div>
								<label>Paid Ticketing</label>
								<p>Sell tickets for your event</p>
							</div>
							<Link role="button" to={PATHNAMES.Event.TicketingLink(id, language)} className="clear no-style">
								<Icon name={ICONS.ARROW_RIGHT} size={14} color={COLORS.WHITE} />
							</Link>
						</div>
					</>)}
				</div>
			) : (
				<div className="panel-body">
					<div className="temp-card">
						<div className="key-gradient"><Icon name={ICONS.PASSWORD} color={COLORS.BLACK} size={16} /></div>
						<div>
							<p>Turn on Registration to Customize Registration Settings</p>
						</div>
					</div>
				</div>
			)}

			<ModalComponent
				size='small'
				closeable={false}
				cancellable={false}
				open={registrationModalOpen}
				onRequestClose={() => setRegistrationModalOpen(false)}
				title={registrationOn ? "Open the Event to Everyone?" : "Turn on Registration"}
				footer={(
					<>
						<button onClick={() => setRegistrationModalOpen(false)}>Cancel</button>
						<button onClick={handleRegistrationToggle} className="primary">
							{registrationOn
								? "Open Event"
								: "Enable Registration"
							}
						</button>
					</>
				)}
			>
				{registrationOn
					? <p>If you turn the registration off, it will become public and everyone will see the event sessions.</p>
					: <p>Turning on registration will require your visitors to complete a form before coming to the page. A registration page is recommended if you are gating your event or looking to capture more information about attendees.</p>
				}
			</ModalComponent>
		</div>
	);
};

export default RegistrationPanel;